// src/App.js
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { registerPushToken, listenForegroundMessages } from "./utils/notifications";
import logoImg from "./assets/MarelleMemories-Logo.png";

// ---- Custom toast system (used by emitToast throughout the app) ----
import AppToastContainer from "./components/Toast";
import PWAInstallBanner from "./components/PWAInstallBanner";
import PWARebrandBanner from "./components/PWARebrandBanner";
import ErrorBoundary from "./components/ErrorBoundary";

// ---- Layout (eager — needed immediately for auth check) ----
import ProtectedLayout from "./layouts/ProtectedLayout";

// ── Splash content — theme-aware, reads saved preference from localStorage ────
function SplashContent() {
  const savedTheme = localStorage.getItem("theme") || "light";
  const isDark     = savedTheme === "dark";

  const bg          = isDark ? "#1c1c1c"                    : "#faeedd";
  const accent      = isDark ? "#e5b94b"                    : "#9a6e00";
  const tagColor    = isDark ? "rgba(229,185,75,0.55)"      : "rgba(154,110,0,0.55)";
  const spinTrack   = isDark ? "rgba(229,185,75,0.14)"      : "rgba(154,110,0,0.14)";
  const logoShadow  = isDark
    ? "0 8px 32px rgba(0,0,0,0.55)"
    : "0 8px 28px rgba(154,110,0,0.18)";

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", width: "100%", height: "100%",
      background: bg,
    }}>
      <style>{`@keyframes fv-spin { to { transform: rotate(360deg); } }`}</style>

      <img
        src={logoImg}
        alt="Marelle Memories"
        style={{
          width: "min(54vw, 210px)",
          marginBottom: 28,
          filter: `drop-shadow(${logoShadow})`,
        }}
      />

      <h1 style={{
        margin: 0,
        fontSize: "clamp(1.9rem, 6.5vw, 2.8rem)",
        fontWeight: 700,
        color: accent,
        letterSpacing: "-0.01em",
        fontFamily: "Georgia, 'Times New Roman', serif",
      }}>
        Marelle Memories
      </h1>

      <p style={{
        margin: "8px 0 44px",
        fontSize: "clamp(0.72rem, 2.4vw, 0.88rem)",
        color: tagColor,
        letterSpacing: "0.05em",
        textAlign: "center",
        padding: "0 24px",
      }}>
        Your private place for the people who matter.
      </p>

      <div style={{
        width: 30, height: 30, borderRadius: "50%",
        border: `3px solid ${spinTrack}`,
        borderTopColor: accent,
        animation: "fv-spin 0.85s linear infinite",
      }} />
    </div>
  );
}

// ── Overlay: fixed cover with fade-out, auto-unmounts after transition ────────
const FADE_MS   = 480;  // fade-out duration
const MIN_MS    = 2600; // minimum splash display time

function SplashOverlay({ visible }) {
  const [mounted, setMounted] = useState(true);
  useEffect(() => {
    if (!visible) {
      const t = setTimeout(() => setMounted(false), FADE_MS);
      return () => clearTimeout(t);
    }
  }, [visible]);
  if (!mounted) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      opacity: visible ? 1 : 0,
      transition: `opacity ${FADE_MS}ms ease`,
      pointerEvents: visible ? "auto" : "none",
    }}>
      <SplashContent />
    </div>
  );
}

// Suspense fallback (rare: only if lazy chunk stalls beyond splash window)
function BrandedSplash() {
  const isDark = (localStorage.getItem("theme") || "light") === "dark";
  return <div style={{ width: "100vw", height: "100vh", background: isDark ? "#1c1c1c" : "#faeedd" }} />;
}

// ---- Pages (lazy — only loaded when the route is visited) ----
const Login             = lazy(() => import("./pages/Login"));
const Register          = lazy(() => import("./pages/Register"));
const ProfileSetupPage  = lazy(() => import("./pages/ProfileSetupPage"));
const Dashboard         = lazy(() => import("./pages/Dashboard"));
const Profile           = lazy(() => import("./pages/Profile"));
const VaultPage         = lazy(() => import("./pages/VaultPage"));
const MessagesPage      = lazy(() => import("./pages/MessagesPage"));
const ThreadPage        = lazy(() => import("./pages/ThreadPage"));
const InviteAcceptPage  = lazy(() => import("./pages/InviteAcceptPage"));
const SettingsPage      = lazy(() => import("./pages/SettingsPage"));

function App() {
  const [online, setOnline]         = useState(navigator.onLine);
  const [authChecked, setAuthChecked] = useState(false);
  const [minTimeDone, setMinTimeDone] = useState(false);
  const splashVisible = !authChecked || !minTimeDone;
  const authFiredRef  = useRef(false);

  // Minimum display timer
  useEffect(() => {
    const t = setTimeout(() => setMinTimeDone(true), MIN_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const up = () => setOnline(true);
    const dn = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", dn);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", dn); };
  }, []);

  useEffect(() => {
    let unsubForeground = () => {};
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      // Mark auth as resolved on first callback (signed in or not)
      if (!authFiredRef.current) {
        authFiredRef.current = true;
        setAuthChecked(true);
      }
      if (user) {
        await registerPushToken(user.uid);
        unsubForeground = await listenForegroundMessages();
      }
    });
    return () => {
      unsubAuth();
      unsubForeground();
    };
  }, []); // eslint-disable-line

  return (
    <ThemeProvider>
      <ErrorBoundary>
      <SplashOverlay visible={splashVisible} />
      {!online && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: "#1f2937", color: "#f9fafb", textAlign: "center",
          fontSize: "0.82rem", padding: "7px 16px", fontWeight: 500,
          letterSpacing: "0.01em",
        }}>
          📡 You're offline — changes may not save until you reconnect.
        </div>
      )}
      <Router>
        <Suspense fallback={<BrandedSplash />}>
        <Routes>

          {/* -------------------- PUBLIC ROUTES -------------------- */}
          <Route path="/invite" element={<InviteAcceptPage />} />
          <Route path="/setup" element={<ProfileSetupPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* -------------------- PROTECTED ROUTES -------------------- */}
          <Route path="/" element={<ProtectedLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="vault/:vaultId" element={<VaultPage />} />

            {/* Messages — ?tab=approvals deep-links to Approvals tab */}
            <Route path="messages" element={<MessagesPage />} />
            <Route
              path="messages/thread/:vaultId/:threadId"
              element={<ThreadPage />}
            />

            {/* Approvals — redirects to MessagesPage with tab pre-selected */}
            <Route
              path="approvals"
              element={<Navigate to="/messages?tab=approvals" replace />}
            />
          </Route>

          {/* -------------------- FALLBACK -------------------- */}
          <Route path="*" element={<Navigate to="/login" replace />} />

        </Routes>
        </Suspense>

        {/* react-toastify — for any legacy toastify calls */}
        <ToastContainer position="top-right" autoClose={3000} theme="colored" />

        {/* Custom app toast — handles all emitToast() calls throughout the app */}
        <AppToastContainer />

        {/* PWA install prompt — fires when browser deems app installable */}
        <PWAInstallBanner />
        <PWARebrandBanner />

      </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;